import dayjs from "dayjs";

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DAY_LABEL: Record<(typeof DAY_ORDER)[number], string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

function formatRange(range: string): string {
  const [start, end] = range.split("-");

  if (start === "00:00" && end === "24:00") {
    return "24 hrs";
  }

  const from = dayjs(start, "HH:mm").format("h:mma");
  const to = end === "24:00" ? "12:00am" : dayjs(end, "HH:mm").format("h:mma");

  return `${from}–${to}`;
}

/**
 * Turns an open-hours object (`{ monday: ["09:00-17:00"], ... }`) into a compact
 * one-line summary, grouping consecutive days that share the same hours —
 * e.g. "Mon–Fri 9:00am–5:00pm, Sat 9:00am–12:00pm". Returns null when nothing is
 * open (so the row shows its empty state).
 */
export function summariseOpenHours(
  hours: Record<string, string[] | null | undefined> | null | undefined,
): string | null {
  if (!hours) {
    return null;
  }

  const openDays = DAY_ORDER.map((day) => ({ day, ranges: hours[day] })).filter(
    (entry): entry is { day: (typeof DAY_ORDER)[number]; ranges: string[] } =>
      Array.isArray(entry.ranges) && entry.ranges.length > 0,
  );

  if (openDays.length === 0) {
    return null;
  }

  const segments: { startDay: string; endDay: string; times: string }[] = [];

  for (const { day, ranges } of openDays) {
    const times = ranges.map(formatRange).join(", ");
    const last = segments.at(-1);
    const isConsecutive =
      !!last &&
      DAY_ORDER.indexOf(day) ===
        DAY_ORDER.indexOf(last.endDay as (typeof DAY_ORDER)[number]) + 1;

    if (last && last.times === times && isConsecutive) {
      last.endDay = day;
    } else {
      segments.push({ startDay: day, endDay: day, times });
    }
  }

  return segments
    .map((segment) => {
      const dayLabel =
        segment.startDay === segment.endDay
          ? DAY_LABEL[segment.startDay as (typeof DAY_ORDER)[number]]
          : `${DAY_LABEL[segment.startDay as (typeof DAY_ORDER)[number]]}–${DAY_LABEL[segment.endDay as (typeof DAY_ORDER)[number]]}`;

      return `${dayLabel} ${segment.times}`;
    })
    .join(", ");
}
